import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import { useSendWorkOrder } from '../../hooks/useWorkOrder'

/**
 * SendWorkOrderDialog is the intervention page's own entry point to the loop —
 * an ordre de service does not always start from a ticket.
 *
 * It follows the same draft-and-confirm doctrine as the ticket action engine:
 * the manager reviews the message before anything leaves. The provider link is
 * NOT shown for editing — it is minted server-side and appended to the body, so
 * a capability URL can never be mistyped, truncated, or pasted into the wrong
 * work order. Sending also rotates the token, which is why the dialog says so.
 */
export function SendWorkOrderDialog({
    org,
    interventionUid,
    interventionTitle,
    providerEmail,
    open,
    onOpenChange,
}: {
    org: string
    interventionUid: string
    interventionTitle: string
    providerEmail?: string | null
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    const { t } = useTranslation('workorders')
    const send = useSendWorkOrder(org, interventionUid)

    const [to, setTo] = useState('')
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [err, setErr] = useState<string | null>(null)
    const [sentLink, setSentLink] = useState<string | null>(null)
    const [queued, setQueued] = useState(false)

    useEffect(() => {
        if (!open) return
        setErr(null)
        setSentLink(null)
        setQueued(false)
        setTo(providerEmail || '')
        setSubject(`${t('send.title')} — ${interventionTitle}`)
        setBody('')
    }, [open, providerEmail, interventionTitle, t])

    const doSend = () => {
        setErr(null)
        const recipients = to
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        if (recipients.length === 0 || !subject.trim() || !body.trim()) {
            setErr(t('send.incomplete'))
            return
        }
        send.mutate(
            { recipients, subject: subject.trim(), body: body.trim() },
            {
                onSuccess: (res) => {
                    setSentLink(res.portal_url)
                    setQueued(res.email_queued)
                },
                onError: () => setErr(t('send.error')),
            },
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="send-work-order-dialog">
                <DialogHeader>
                    <DialogTitle>{t('send.title')}</DialogTitle>
                    <DialogDescription>{t('send.hint')}</DialogDescription>
                </DialogHeader>

                {sentLink ? (
                    <div className="space-y-2" data-testid="send-work-order-result">
                        <p className="text-sm" data-testid="send-work-order-outcome">
                            {queued ? t('send.queued') : t('send.link_only')}
                        </p>
                        <Input readOnly value={sentLink} data-testid="send-work-order-link" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="wo-send-to">To</Label>
                            <Input
                                id="wo-send-to"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                data-testid="send-work-order-to"
                            />
                            {!providerEmail ? (
                                <p className="text-xs text-muted-foreground">{t('send.no_provider')}</p>
                            ) : null}
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="wo-send-subject">Subject</Label>
                            <Input
                                id="wo-send-subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                data-testid="send-work-order-subject"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="wo-send-body">Message</Label>
                            <Textarea
                                id="wo-send-body"
                                rows={6}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                data-testid="send-work-order-body"
                            />
                        </div>
                        {err ? (
                            <p className="text-sm text-destructive" data-testid="send-work-order-error">
                                {err}
                            </p>
                        ) : null}
                    </div>
                )}

                <DialogFooter>
                    {sentLink ? (
                        <Button onClick={() => onOpenChange(false)} data-testid="send-work-order-close">
                            OK
                        </Button>
                    ) : (
                        <Button onClick={doSend} disabled={send.isPending} data-testid="send-work-order-send">
                            {t('send.button')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
